import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, CheckCircle2, Circle, ArrowRight, Sparkles } from "lucide-react";
import { useLocation } from "react-router-dom";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { shouldShowMobileShell } from "../mobile/mobileShellRoutes";

type Category = "individual" | "couple" | "family";

export type Child = {
  age?: number | string;
  child_category?: "child_0_5" | "child_6_12" | "teen_13_17" | "adult_18_plus" | string;
};

export type LiveSummaryForm = {
  category: Category;
  boardingType?: "camp" | "hotel";
  includeSpouse: boolean;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  spouseFirstName?: string;
  spouseLastName?: string;
  spouseEmail?: string;
  spouseMobile?: string;
  children: Child[];
  otherAdults: { first_name?: string; last_name?: string; attendee_type?: "adult" | "student" }[];
  primaryAttendeeType?: "adult" | "student";
};

export type SummaryLine = {
  label: string;
  amount: number;
  variant: "base" | "spouse" | "upgrade" | "free" | "included" | "adult" | "student" | "offcampus";
};

// Individual attendee pricing (keep in sync with ConferenceRegistration).
const INDIVIDUAL_FEES: Record<"adult" | "student", number> = { adult: 125, student: 125 };
const feeFor = (t?: "adult" | "student") => INDIVIDUAL_FEES[t === "student" ? "student" : "adult"];

type Props = {
  form: LiveSummaryForm;
  computedPrice: number;
  familyBase: number;
  isHotel: boolean;
  hotelBillablePersons?: number;
  hotelFreeChildren?: number;
  paymentChoice: string;
  paymentScrollTargetId: string;
  submitted: boolean;
  submitting: boolean;
};

const CATEGORY_LABEL: Record<Category, string> = {
  individual: "Individual",
  couple: "Couple",
  family: "Family",
};

function buildLines(
  form: LiveSummaryForm,
  isHotel: boolean,
  familyBase: number,
  hotelBillablePersons?: number,
  hotelFreeChildren?: number,
): SummaryLine[] {
  const lines: SummaryLine[] = [];
  if (isHotel) {
    lines.push({ label: "Accommodation: Off Campus (Self-Arranged)", amount: 0, variant: "offcampus" });
    // $75 per person for adults & teens (children 0–12 free). Use authoritative counts from parent.
    const billable = Math.max(1, hotelBillablePersons ?? 1);
    lines.push({
      label: `Conference Registration & Food (6 Meals) — ${billable} × $75`,
      amount: 75 * billable,
      variant: "base",
    });
    const freeKids = hotelFreeChildren ?? 0;
    if (freeKids > 0) {
      lines.push({ label: `Children 0–12 (${freeKids})`, amount: 0, variant: "free" });
    }
    return lines;
  }
  if (form.category === "individual") {
    const primaryType = form.primaryAttendeeType === "student" ? "student" : "adult";
    const primaryLabel = primaryType === "student" ? "Primary Student" : "Primary Individual";
    lines.push({ label: primaryLabel, amount: feeFor(primaryType), variant: "base" });
    let studentIdx = 0;
    let adultIdx = 1; // primary counts as Adult #1
    form.otherAdults.forEach((a, i) => {
      const t = a.attendee_type === "student" ? "student" : "adult";
      const name = [a.first_name, a.last_name].filter(Boolean).join(" ").trim();
      let label: string;
      if (t === "student") {
        studentIdx += 1;
        label = name ? `Student — ${name}` : `Student #${studentIdx}`;
      } else {
        adultIdx += 1;
        label = name ? `Adult — ${name}` : `Adult #${adultIdx}`;
      }
      lines.push({ label, amount: feeFor(t), variant: t });
    });
  } else if (form.category === "couple") {
    lines.push({ label: "Primary Registration", amount: 125, variant: "base" });
    if (form.includeSpouse) lines.push({ label: "Spouse Registration", amount: 125, variant: "spouse" });
  } else {
    lines.push({ label: "Primary Registration", amount: 125, variant: "base" });
    if (form.includeSpouse) lines.push({ label: "Spouse Registration", amount: 125, variant: "spouse" });
    const prior = 125 + (form.includeSpouse ? 125 : 0);
    const upgrade = familyBase - prior;
    if (upgrade > 0) {
      lines.push({ label: "Family Upgrade (child 6–17 included)", amount: upgrade, variant: "upgrade" });
    }
    form.children.forEach((c) => {
      const age = (c as any).age;
      const ageTxt = age != null && age !== "" ? ` (Age ${age})` : "";
      const isAdult = c.child_category === "adult_18_plus" || (age != null && age !== "" && Number(age) >= 18);
      if (isAdult) {
        lines.push({ label: `Additional Adult${ageTxt}`, amount: 125, variant: "adult" });
      } else if (c.child_category === "child_0_5") {
        lines.push({ label: `Child${ageTxt}`, amount: 0, variant: "free" });
      } else {
        const tag = c.child_category === "teen_13_17" ? "Teenager" : "Child";
        lines.push({ label: `${tag}${ageTxt}`, amount: 0, variant: "included" });
      }
    });
    form.otherAdults.forEach((_, i) => {
      lines.push({ label: `Additional Adult #${i + 1}`, amount: 125, variant: "adult" });
    });
  }
  return lines;
}

function Badge({ variant, amount }: { variant: SummaryLine["variant"]; amount: number }) {
  const base = "inline-flex items-center rounded-full text-[10.5px] font-semibold px-2 py-0.5 whitespace-nowrap";
  if (variant === "free") return <span className={`${base} bg-emerald-100 text-emerald-700`}>FREE</span>;
  if (variant === "included") return <span className={`${base} bg-sky-100 text-sky-700`}>INCLUDED</span>;
  if (variant === "upgrade") return <span className={`${base} bg-amber-100 text-amber-800`}>+${amount} UPGRADE</span>;
  if (variant === "adult") return <span className={`${base} bg-rose-100 text-rose-700`}>+${amount} ADULT</span>;
  if (variant === "student") return <span className={`${base} bg-violet-100 text-violet-700`}>+${amount} STUDENT</span>;
  if (variant === "offcampus") return <span className={`${base} bg-slate-100 text-slate-700`}>SELF-ARRANGED</span>;
  if (variant === "spouse") return <span className={`${base} bg-indigo-100 text-indigo-700`}>+${amount}</span>;
  return <span className={`${base} bg-slate-200 text-slate-800`}>${amount}</span>;
}

function useProgress(form: LiveSummaryForm, paymentChoice: string) {
  return useMemo(() => {
    const steps = [
      { key: "category", label: "Category", done: !!form.category },
      { key: "boarding", label: "Boarding", done: !!form.boardingType },
      {
        key: "personal",
        label: "Personal Info",
        done: !!(form.firstName && form.lastName && form.email && form.mobile),
      },
      {
        key: "family",
        label: "Family Details",
        done:
          form.category === "individual"
            ? !!(form.firstName && form.lastName)
            : form.category === "couple"
              ? !form.includeSpouse || !!(form.spouseFirstName && form.spouseLastName)
              : !form.includeSpouse || !!(form.spouseFirstName && form.spouseLastName),
      },
      { key: "payment", label: "Payment", done: !!paymentChoice },
    ];
    const completed = steps.filter((s) => s.done).length;
    const percent = Math.round((completed / steps.length) * 100);
    const ready = percent === 100;
    return { steps, completed, percent, ready };
  }, [form, paymentChoice]);
}

function SummaryBody({
  form,
  computedPrice,
  isHotel,
  familyBase,
  hotelBillablePersons,
  hotelFreeChildren,
  paymentChoice,
  paymentScrollTargetId,
  submitting,
}: Props) {
  const lines = useMemo(
    () => buildLines(form, isHotel, familyBase, hotelBillablePersons, hotelFreeChildren),
    [form, isHotel, familyBase, hotelBillablePersons, hotelFreeChildren],
  );
  const { steps, percent, ready } = useProgress(form, paymentChoice);

  const scrollToPayment = () => {
    const el = document.getElementById(paymentScrollTargetId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Progress */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
            {ready ? "Ready for Payment" : "Registration Progress"}
          </span>
          <span className="text-[11px] font-semibold text-slate-500 tabular-nums">{percent}%</span>
        </div>
        <Progress value={percent} className="h-1.5" />
        <ul className="mt-2 grid grid-cols-1 gap-0.5">
          {steps.map((s) => (
            <li key={s.key} className="flex items-center gap-1.5 text-[11.5px] text-slate-600">
              {s.done ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              ) : (
                <Circle className="w-3.5 h-3.5 text-slate-300 shrink-0" />
              )}
              <span className={s.done ? "text-slate-700" : "text-slate-500"}>{s.label}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Selections */}
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div className="rounded-lg bg-white border border-slate-200 px-2.5 py-1.5">
          <div className="text-slate-500 uppercase tracking-wide text-[9.5px]">Category</div>
          <div className="font-semibold text-slate-900">{CATEGORY_LABEL[form.category]}</div>
        </div>
        <div className="rounded-lg bg-white border border-slate-200 px-2.5 py-1.5">
          <div className="text-slate-500 uppercase tracking-wide text-[9.5px]">Boarding</div>
          <div className="font-semibold text-slate-900">
            {form.boardingType === "hotel" ? "Off Campus" : form.boardingType === "camp" ? "Camp" : "—"}
          </div>
        </div>
      </div>

      {/* Lines */}
      <div>
        <div className="text-slate-600 text-[10.5px] uppercase tracking-wide font-semibold mb-1.5">
          Price Breakdown
        </div>
        {form.boardingType ? (
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white overflow-hidden">
            {lines.map((l, i) => (
              <li
                key={`${l.label}-${i}`}
                className="flex items-center justify-between gap-2 px-2.5 py-2 text-[12.5px] animate-in fade-in slide-in-from-top-1 duration-200"
              >
                <span className="text-slate-800 flex-1 min-w-0 truncate">{l.label}</span>
                <Badge variant={l.variant} amount={l.amount} />
              </li>
            ))}
          </ul>
        ) : (
          <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 text-amber-800 text-[12px] px-3 py-2">
            Choose <strong>Boarding Type</strong> to calculate pricing.
          </div>
        )}
      </div>

      {isHotel && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-amber-900 text-[11px]">
          Hotel stay is booked & paid separately. Not included in this fee.
        </div>
      )}

      {/* Total */}
      <div
        key={computedPrice}
        className="pt-3 border-t border-slate-200 flex items-center justify-between animate-in fade-in zoom-in-95 duration-300"
      >
        <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
          Estimated Total
        </span>
        <span className="text-2xl font-bold bg-gradient-to-r from-sky-600 to-indigo-600 bg-clip-text text-transparent tabular-nums">
          ${computedPrice}
        </span>
      </div>

      <Button
        type="button"
        onClick={scrollToPayment}
        disabled={submitting}
        className="w-full bg-gradient-to-r from-sky-600 to-amber-500 hover:from-sky-700 hover:to-amber-600 text-white shadow-md"
      >
        {ready ? "Proceed to Payment" : "Continue to Payment"}
        <ArrowRight className="w-4 h-4 ml-1" />
      </Button>

      <p className="text-[10.5px] text-slate-500 leading-snug text-center">
        Summary updates live as you make selections. You can adjust any field above before submitting.
      </p>
    </div>
  );
}

export default function LiveRegistrationSummary(props: Props) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const hasMobileShell = shouldShowMobileShell(location.pathname);
  const { form, computedPrice, paymentChoice } = props;
  const { percent, ready } = useProgress(form, paymentChoice);

  // Close mobile drawer on escape
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMobileOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  return (
    <>
      {/* Desktop sticky sidebar */}
      <aside className="hidden lg:block lg:sticky lg:top-20 lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 shadow-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <h3 className="font-semibold text-slate-900 text-sm">Registration Summary</h3>
          </div>
          <SummaryBody {...props} />
        </div>
      </aside>

      {/* Mobile floating bottom card */}
      <div className="lg:hidden">
        {/* Spacer so floating bar doesn't cover form bottom */}
        <div aria-hidden className={hasMobileShell ? "h-36 md:h-20" : "h-20"} />
        <div
          className={`fixed inset-x-0 z-40 pointer-events-none ${
            hasMobileShell
              ? "bottom-[calc(4rem+var(--safe-area-bottom,0px))] md:bottom-0"
              : "bottom-0"
          }`}
        >
          <div className="mx-auto max-w-2xl px-3 pb-3 pointer-events-auto">
            <div className="rounded-2xl border border-slate-200 bg-white/95 backdrop-blur shadow-2xl overflow-hidden">
              {/* Collapsed bar */}
              <button
                type="button"
                onClick={() => setMobileOpen((o) => !o)}
                aria-expanded={mobileOpen}
                className="w-full flex items-center justify-between gap-3 px-4 py-3"
              >
                <div className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      {ready ? "Ready" : `${percent}% Complete`}
                    </span>
                    {!ready && (
                      <span className="text-[10px] text-slate-400">·</span>
                    )}
                    {!ready && (
                      <span className="text-[10px] text-slate-500 truncate">
                        {form.boardingType ? "Live estimate" : "Pick boarding to price"}
                      </span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-[10.5px] font-semibold text-slate-600 uppercase tracking-wide">
                      Estimated Total
                    </span>
                    <span
                      key={computedPrice}
                      className="text-xl font-bold bg-gradient-to-r from-sky-600 to-indigo-600 bg-clip-text text-transparent tabular-nums animate-in fade-in zoom-in-95 duration-300"
                    >
                      ${computedPrice}
                    </span>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700">
                  {mobileOpen ? "Hide" : "View"}
                  {mobileOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                </span>
              </button>

              {/* Expanded body */}
              {mobileOpen && (
                <div className="px-4 pb-4 border-t border-slate-100 max-h-[70vh] overflow-y-auto">
                  <div className="pt-3">
                    <SummaryBody {...props} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
